import { auth } from './core';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  deleteUser,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  OAuthProvider,
  UserCredential,
  User,
  // AuthError is defined locally
  signInWithCredential,
  linkWithCredential,
  AuthCredential
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple, SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';
import {
  getData,
  updateData,
  removeData,
  updateMultipleSafe
} from './api-wrapper.service';
import { UserProfile as ProfileServiceUserProfile } from './profile.service';
import { goOnline } from 'firebase/database';
import { database } from './core';
import perksService from '../perks.service';
import apiClient from '../api/apiClient';
import logger from '../../utils/logger';
// Removed simple-auth-persistence - auth state now handled by Firebase naturally

// Types
export interface RegisterUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  photoURL?: string;
}
// Use the UserProfile from profile.service.ts to ensure consistency
export type UserProfile = ProfileServiceUserProfile;
export interface AuthError {
  code: string;
  message: string;
}

// Removed complex auth monitoring - keep it simple

// Backend API URL for username lookup
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://marune-backend.onrender.com';

// Simplified email/username login
export const loginWithEmail = async (emailOrUsername: string, password: string): Promise<UserCredential> => {
  try {
    let email = emailOrUsername;

    // If username (no @), look up email from backend
    if (!emailOrUsername.includes('@')) {
      logger.info('Username detected, looking up email from backend');
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/lookup-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: emailOrUsername }),
        });

        if (response.ok) {
          const data = await response.json();
          email = data.email;
          logger.info('Email lookup successful');
        } else if (response.status === 404) {
          // Username not found
          const error = new Error('Username not found');
          (error as any).code = 'auth/user-not-found';
          throw error;
        } else {
          // Backend error, log but continue with fallback
          logger.warn('Email lookup failed, trying fallback');
          email = `${emailOrUsername}@marune.app`;
        }
      } catch (lookupError: any) {
        // If it's our custom error, throw it
        if (lookupError.code === 'auth/user-not-found') {
          throw lookupError;
        }
        // Network error - try fallback
        logger.warn('Email lookup network error, trying fallback:', lookupError);
        email = `${emailOrUsername}@marune.app`;
      }
    }

    // Simple Firebase auth
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Ensure database is online
    if (database) {
      goOnline(database);
    }

    return result;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};

// Removed complex retry logic - let Firebase handle network issues naturally

// Helper function to generate random username (matching original app)
const generateRandomUsername = (): string => {
  // Generate username like: anon99127391247
  // Format: anon + 2 random digits + timestamp substring
  const randomDigits = Math.random().toString().substr(2, 2);
  const timestamp = Date.now().toString().substring(2);
  return `anon${randomDigits}${timestamp}`;
};

// Helper function to assign random username with retry logic (matching original app)
const assignRandomUsername = async (uid: string, maxAttempts: number = 50): Promise<string> => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const randomUsername = generateRandomUsername();

    // Check if username is available (user is authenticated at this point)
    const isAvailable = await isUsernameAvailable(randomUsername);

    if (isAvailable) {
      // Username is available, claim it
      return randomUsername;
    }

    // Username taken, try again
    attempts++;
    logger.info(`Username ${randomUsername} taken, retrying... (attempt ${attempts}/${maxAttempts})`);
  }

  // Max attempts reached
  const error = new Error('Failed to generate a unique username after multiple attempts');
  (error as any).code = 'auth/username-generation-failed';
  throw error;
};

// Registration with email/password (matching original app flow)
export const registerWithEmail = async (userData: RegisterUserData): Promise<UserCredential> => {
  try {
    // Step 1: Create Firebase auth user FIRST (matching original app)
    logger.info('Creating Firebase auth user');
    const result = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
    const uid = result.user.uid;
    logger.info('Firebase auth user created successfully:', uid);

    // Step 2: Generate random username AFTER authentication (matching original app)
    logger.info('Generating random username');
    const username = await assignRandomUsername(uid);
    logger.info('Random username generated:', username);

    // Step 3: Create display name
    const displayName = userData.displayName || `${userData.firstName} ${userData.lastName}`;

    // Step 4: Create user profile in database - matching original app structure
    const userProfile = {
      uid,
      email: userData.email,
      username,
      usernameLower: username.toLowerCase(), // For case-insensitive lookups
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName,
      displayNameLower: displayName.toLowerCase(), // For case-insensitive lookups
      photoURL: userData.photoURL || '',
      profilePhotoURL: userData.photoURL || '', // Original app field name
      dateCreated: Date.now(),
      dateModified: Date.now(),
      isPeakSub: false,
      preferredWeightUM: 'kg',
      // Fields from original app that should be initialized
      bio: null,
      age: null,
      dateOfBirth: null,
      gender: null,
      instructor: null,
      school: null,
      beltDesignation: null,
      stripesCount: 0,
      // CRITICAL: Initialize pushToken for social function compatibility
      pushToken: null, // Will be set when user enables notifications
      socialMediaProfiles: {
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        website: ''
      },
      preferences: {
        theme: 'system',
        notifications: true,
        privacy: {
          profileVisibility: 'public',
          showTrainingSessions: true,
          showCompetitions: true
        }
      }
    };

    // Step 5: Create username mapping for uniqueness
    logger.info('Creating user profile and username mapping');
    const updates: Record<string, any> = {
      [`/users/${uid}`]: userProfile,
      [`/usernames/${username}`]: { uid }
    };

    // Use safer version to avoid root permission errors
    await updateMultipleSafe(updates);
    logger.info('User profile and username mapping created successfully');

    // CRITICAL FIX (Phase 1.3): Verify profile was actually created
    // This catches silent failures in updateMultipleSafe
    const maxVerifyAttempts = 3;
    let profileVerified = false;

    for (let attempt = 1; attempt <= maxVerifyAttempts; attempt++) {
      try {
        // Wait a bit for the write to propagate
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));

        const verifyProfile = await getData(`/users/${uid}`);
        if (verifyProfile && verifyProfile.uid === uid) {
          logger.info(`✅ Profile verified successfully on attempt ${attempt}`);
          profileVerified = true;
          break;
        } else {
          logger.warn(`Profile verification attempt ${attempt}: profile not found or incomplete`);
        }
      } catch (verifyError) {
        logger.warn(`Profile verification attempt ${attempt} failed:`, verifyError);
      }
    }

    if (!profileVerified) {
      // Profile creation failed silently - this is a critical error
      logger.error('❌ CRITICAL: Profile creation verification failed after registration');
      // Attempt to create the profile again using individual updates
      try {
        await updateData(`/users/${uid}`, userProfile);
        await updateData(`/usernames/${username}`, { uid });
        logger.info('Retried profile creation with individual updates');

        // Verify again
        const retryVerify = await getData(`/users/${uid}`);
        if (!retryVerify || retryVerify.uid !== uid) {
          const error = new Error('Failed to create user profile after multiple attempts');
          (error as any).code = 'auth/profile-creation-failed';
          throw error;
        }
      } catch (retryError) {
        logger.error('Retry profile creation failed:', retryError);
        const error = new Error('Registration partially completed - profile creation failed. Please contact support.');
        (error as any).code = 'auth/profile-creation-failed';
        throw error;
      }
    }

    // Step 6: Unlock new user perks
    try {
      await perksService.unlockNewUserPerks(uid);
    } catch (perkError) {
      logger.error('Error unlocking new user perks:', perkError);
      // Don't fail registration if perk unlocking fails
    }

    // Step 7: Send email verification
    await sendEmailVerification(result.user);

    // Step 8: Ensure Firebase Database is online after successful registration
    // This is critical for permissions to work properly
    try {
      if (database) {
        logger.info('Bringing Firebase database online after successful registration');
        goOnline(database);
      }
    } catch (goOnlineError) {
      logger.error('Error bringing database online:', goOnlineError);
      // Continue anyway - registration was successful
    }

    return result;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Reset password
export const resetPassword = async (email: string): Promise<void> => {
  try {
    return sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Confirm password reset
export const confirmResetPassword = async (code: string, newPassword: string): Promise<void> => {
  try {
    return confirmPasswordReset(auth, code, newPassword);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Verify password reset code
export const verifyResetCode = async (code: string): Promise<string> => {
  try {
    return verifyPasswordResetCode(auth, code);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Removed complex auth restoration logic - Firebase handles this automatically

// Sign out
export const logout = async (): Promise<void> => {
  try {
    logger.info('Signing out from Firebase auth');

    // Clean up push notifications before signing out
    try {
      const { cleanupPushNotifications } = await import('./push-notification.service');
      await cleanupPushNotifications();
    } catch (pushError) {
      logger.error('Error cleaning up push notifications:', pushError);
      // Continue with logout even if push cleanup fails
    }

    // Clear comment caches to prevent memory leaks
    try {
      const { clearAllCommentsCaches } = await import('../../hooks/useComments');
      clearAllCommentsCaches();
    } catch (cacheError) {
      logger.error('Error clearing comment caches:', cacheError);
      // Continue with logout even if cache cleanup fails
    }

    return signOut(auth);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};
// Listen to auth state changes
export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) => {
  // Set up the standard Firebase auth listener
  return onAuthStateChanged(auth, callback);
};
// Send email verification
export const sendVerificationEmail = async (user: User = auth.currentUser!): Promise<void> => {
  try {
    if (!user) {
      const error = new Error('User not found');
      (error as any).code = 'auth/user-not-found';
      throw error;
    }
    
    return sendEmailVerification(user);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Get user profile data - simplified version
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const profileData = await getData(`/users/${uid}`);
    return profileData as UserProfile;
  } catch (error: any) {
    logger.error('Error fetching user profile:', error);
    return null;
  }
};
// Get user by username
export const getUserByUsername = async (username: string): Promise<UserProfile | null> => {
  try {
    const usernameData = await getData(`/usernames/${username}`);
    if (!usernameData || !usernameData.uid) {
      return null;
    }
    
    return getUserProfile(usernameData.uid);
  } catch (error: any) {
    logger.error('Error fetching user by username:', error);
    throw error;
  }
};
// Check if username is available
export const isUsernameAvailable = async (username: string): Promise<boolean> => {
  try {
    const usernameData = await getData(`/usernames/${username}`);
    return !usernameData;
  } catch (error: any) {
    logger.error('Error checking username availability:', error);
    throw error;
  }
};
// Update user profile
export const updateUserProfile = async (
  uid: string, 
  profileData: Partial<UserProfile>
): Promise<UserProfile> => {
  try {
    const currentUser = auth.currentUser;
    
    // Check if this is the current user
    if (currentUser && currentUser.uid === uid) {
      // Check if username is being changed
      if (profileData.username) {
        const currentProfile = await getUserProfile(uid);
        if (currentProfile && currentProfile.username !== profileData.username) {
          // Check if new username is available
          const isAvailable = await isUsernameAvailable(profileData.username);
          if (!isAvailable) {
            const error = new Error('This username is already taken.');
            (error as any).code = 'auth/username-already-in-use';
            throw error;
          }
          
          // Update username mapping
          const updates: Record<string, any> = {
            [`/usernames/${profileData.username}`]: { uid },
            [`/usernames/${currentProfile.username}`]: null
          };
          
          // Use safer version to avoid root permission errors
          await updateMultipleSafe(updates);
        }
      }
      
      // Add lowercase fields for case-insensitive searches
      // Removed duplicate declaration - now using profileUpdateData below
      
      // Create a new object for the updated profile data, using Record<string, any> for dynamic properties
      const profileUpdateData: Record<string, any> = { 
        ...profileData,
        dateModified: Date.now() 
      };
      
      if (profileData.username) {
        profileUpdateData.usernameLower = profileData.username.toLowerCase();
      }
      
      if (profileData.displayName) {
        profileUpdateData.displayNameLower = profileData.displayName.toLowerCase();
      }
      
      // Update user profile in database
      await updateData(`/users/${uid}`, profileUpdateData);
    } else {
      // Not the current user, throw error
      const error = new Error('You do not have permission to update this profile.');
      (error as any).code = 'auth/permission-denied';
      throw error;
    }
    
    // Return updated profile
    const updatedProfile = await getUserProfile(uid);
    if (!updatedProfile) {
      const error = new Error('User profile not found after update.');
      (error as any).code = 'auth/user-not-found';
      throw error;
    }
    
    return updatedProfile;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Update user email
export const updateUserEmail = async (
  currentPassword: string, 
  newEmail: string
): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      const error = new Error('User not found');
      (error as any).code = 'auth/user-not-found';
      throw error;
    }
    
    if (!user.email) {
      const error = new Error('Current user does not have an email');
      (error as any).code = 'auth/invalid-email';
      throw error;
    }
    
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update email
    await updateEmail(user, newEmail);
    
    // Update email in database
    await updateData(`/users/${user.uid}`, {
      email: newEmail,
      emailVerified: false,
      dateModified: Date.now()
    });
    
    // Send verification email
    await sendEmailVerification(user);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Update user password
export const updateUserPassword = async (
  currentPassword: string, 
  newPassword: string
): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      const error = new Error('User not found');
      (error as any).code = 'auth/user-not-found';
      throw error;
    }
    
    if (!user.email) {
      const error = new Error('Current user does not have an email');
      (error as any).code = 'auth/invalid-email';
      throw error;
    }
    
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update password
    await updatePassword(user, newPassword);
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Delete user account
export const deleteUserAccount = async (
  currentPassword: string
): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      const error = new Error('User not found');
      (error as any).code = 'auth/user-not-found';
      throw error;
    }
    
    if (!user.email) {
      const error = new Error('Current user does not have an email');
      (error as any).code = 'auth/invalid-email';
      throw error;
    }
    
    // Get user profile for cleanup
    const profile = await getUserProfile(user.uid);
    
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Prepare deletion operations
    const uid = user.uid;

    // Clean up RTDB data FIRST, while still authenticated — once the auth
    // account is deleted these writes are rejected by the security rules,
    // which used to orphan the profile and username mapping.
    // (Same order as deleteClearMatAccount below.)
    if (profile && profile.username) {
      await removeData(`/usernames/${profile.username}`);
    }
    await removeData(`/users/${uid}`);

    // Delete user from auth last
    await deleteUser(user);

    // Note: In a production app, you may want to use Cloud Functions to
    // handle additional cleanup of related data like user uploads, posts, etc.
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Delete ClearMat account (no password required — works for both email and Apple Sign-In)
export const deleteClearMatAccount = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    const error = new Error('User not found');
    (error as any).code = 'auth/user-not-found';
    throw error;
  }

  // Capture uid and profile before any deletions
  const uid = user.uid;
  const profile = await getUserProfile(uid);

  // 1. Delete ClearMat PostgreSQL data via backend (requires auth token)
  await apiClient.delete('/api/clearmat/users/me');

  // 2. Clean up Firebase RTDB data (username mapping + user profile)
  if (profile?.username) {
    await removeData(`/usernames/${profile.username}`);
  }
  await removeData(`/users/${uid}`);

  // 3. Delete Firebase Auth account last
  // If this fails with requires-recent-login, steps 1-2 are already done.
  // On retry (after re-login), steps 1-2 will be no-ops (data already gone).
  await deleteUser(user);
};
// Sign in with Google
export const loginWithGoogle = async (useRedirect: boolean = false): Promise<UserCredential | void> => {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    
    if (useRedirect) {
      await signInWithRedirect(auth, provider);
      // No return value for redirect
      return;
    } else {
      const result = await signInWithPopup(auth, provider);
      await handleSocialAuthSuccess(result);
      return result;
    }
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Get redirect result (for social login)
export const getRedirectLoginResult = async (): Promise<UserCredential | null> => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      await handleSocialAuthSuccess(result);
    }
    return result;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Sign in with Facebook
export const loginWithFacebook = async (useRedirect: boolean = false): Promise<UserCredential | void> => {
  try {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    
    if (useRedirect) {
      await signInWithRedirect(auth, provider);
      // No return value for redirect
      return;
    } else {
      const result = await signInWithPopup(auth, provider);
      await handleSocialAuthSuccess(result);
      return result;
    }
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Sign in with Twitter
export const loginWithTwitter = async (useRedirect: boolean = false): Promise<UserCredential | void> => {
  try {
    const provider = new TwitterAuthProvider();
    
    if (useRedirect) {
      await signInWithRedirect(auth, provider);
      // No return value for redirect
      return;
    } else {
      const result = await signInWithPopup(auth, provider);
      await handleSocialAuthSuccess(result);
      return result;
    }
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Helper function to generate a random nonce for Apple Sign-In
const generateNonce = (length: number = 32): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
};

// Helper function to SHA256 hash the nonce (required by Apple Sign-In)
const sha256 = async (plain: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Sign in with Apple - Uses native flow on iOS for better UX (matching original app)
export const loginWithApple = async (useRedirect: boolean = false): Promise<UserCredential | void> => {
  try {
    const platform = Capacitor.getPlatform();

    // Use native Apple Sign-In on iOS (matching original Marune app behavior)
    if (platform === 'ios') {
      logger.info('Using native Apple Sign-In flow on iOS');

      // Generate a nonce for security (required by Firebase)
      const rawNonce = generateNonce();
      const hashedNonce = await sha256(rawNonce);
      logger.info('Generated nonce for Apple Sign-In');

      // Call the native Apple Sign-In through Capacitor community plugin
      // This triggers the native iOS Apple Sign-In sheet
      const response: SignInWithAppleResponse = await SignInWithApple.authorize({
        clientId: 'social.marune.marune', // Your app's bundle ID
        redirectURI: 'https://marune-da0a1.firebaseapp.com/__/auth/handler', // Firebase auth handler
        scopes: 'email name',
        nonce: hashedNonce // Pass hashed nonce to Apple
      });

      logger.info('Native Apple Sign-In response received', {
        hasIdentityToken: !!response.response?.identityToken,
        hasGivenName: !!response.response?.givenName,
        hasFamilyName: !!response.response?.familyName,
        hasEmail: !!response.response?.email
      });

      const { identityToken, givenName, familyName, email: appleEmail } = response.response;

      if (!identityToken) {
        throw new Error('No identity token returned from Apple Sign-In');
      }

      // Try to get email from Apple response or decode from JWT token
      let emailToCheck = appleEmail;
      if (!emailToCheck) {
        // Apple may not return email on subsequent sign-ins
        // Try to decode email from the identity token (JWT)
        try {
          const tokenParts = identityToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            emailToCheck = payload.email;
            logger.info('Extracted email from Apple JWT token:', emailToCheck);
          }
        } catch (jwtError) {
          logger.warn('Could not decode email from Apple JWT:', jwtError);
        }
      }

      // NOTE: We skip pre-checking email existence because fetchSignInMethodsForEmail
      // is broken with Firebase v11+ Email Enumeration Protection (always returns []).
      // Instead, we sign in directly. signInWithCredential will:
      //   - Sign in existing Apple users
      //   - Create new accounts for first-time Apple users
      // Firebase handles account-exists-with-different-credential errors automatically.

      // Create Firebase credential with the Apple identity token and raw nonce
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: identityToken,
        rawNonce: rawNonce // Pass raw (unhashed) nonce to Firebase
      });

      logger.info('Created Firebase credential from Apple token');

      // Sign in to Firebase with the credential
      const userCredential = await signInWithCredential(auth, credential);
      logger.info('Firebase sign-in successful', { uid: userCredential.user.uid });

      // Handle the Apple auth success - create profile if new user
      // Pass Apple's name info since Firebase doesn't always get it from Apple
      await handleAppleAuthSuccess(userCredential.user, {
        givenName: givenName || null,
        familyName: familyName || null,
        displayName: givenName && familyName ? `${givenName} ${familyName}` : null
      });

      return userCredential;
    }

    // Web/Android fallback - use popup flow
    logger.info('Using web popup Apple Sign-In flow');
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    if (useRedirect) {
      await signInWithRedirect(auth, provider);
      // No return value for redirect
      return;
    } else {
      const result = await signInWithPopup(auth, provider);
      await handleSocialAuthSuccess(result);
      return result;
    }
  } catch (error: any) {
    logger.error('Apple Sign-In error:', error);
    // Don't format our custom errors - pass them through
    if (error.code === 'auth/apple-account-not-found') {
      throw error;
    }
    throw formatAuthError(error);
  }
};

/**
 * Complete Apple Sign-In after user confirms they want to create a new account
 * Called when user confirms "Create New Account" after seeing the no-account-found dialog
 */
export const completeAppleSignInWithNewAccount = async (
  credentialData: {
    identityToken: string;
    rawNonce: string;
    givenName?: string | null;
    familyName?: string | null;
  }
): Promise<UserCredential> => {
  try {
    const { identityToken, rawNonce, givenName, familyName } = credentialData;

    // Create Firebase credential
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: identityToken,
      rawNonce: rawNonce
    });

    // Sign in to Firebase
    const userCredential = await signInWithCredential(auth, credential);
    logger.info('Firebase sign-in successful for new Apple account', { uid: userCredential.user.uid });

    // Create profile for new user
    await handleAppleAuthSuccess(userCredential.user, {
      givenName: givenName || null,
      familyName: familyName || null,
      displayName: givenName && familyName ? `${givenName} ${familyName}` : null
    });

    return userCredential;
  } catch (error: any) {
    logger.error('Error completing Apple Sign-In:', error);
    throw formatAuthError(error);
  }
};

/**
 * Link Apple Sign-In to an existing logged-in account
 * User must be logged in first with their original auth method
 */
export const linkAppleToAccount = async (): Promise<UserCredential> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to link your Apple account');
    }

    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
      // Generate nonce for security
      const rawNonce = generateNonce();
      const hashedNonce = await sha256(rawNonce);

      // Call native Apple Sign-In
      const response: SignInWithAppleResponse = await SignInWithApple.authorize({
        clientId: 'social.marune.marune',
        redirectURI: 'https://marune-da0a1.firebaseapp.com/__/auth/handler',
        scopes: 'email name',
        nonce: hashedNonce
      });

      const { identityToken } = response.response;

      if (!identityToken) {
        throw new Error('No identity token returned from Apple Sign-In');
      }

      // Create credential
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: identityToken,
        rawNonce: rawNonce
      });

      // Link credential to current user
      const result = await linkWithCredential(currentUser, credential);
      logger.info('Successfully linked Apple to account', { uid: result.user.uid });

      return result;
    } else {
      // Web fallback - use popup
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithPopup(auth, provider);
      // For web, signInWithPopup already handles linking if user is logged in
      // But we need to use linkWithPopup instead - for now just return the result
      logger.info('Apple linked via popup', { uid: result.user.uid });
      return result;
    }
  } catch (error: any) {
    logger.error('Error linking Apple account:', error);
    throw formatAuthError(error);
  }
};

/**
 * Check what auth providers are linked to the current user
 */
export const getLinkedProviders = (): string[] => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return [];
  }
  return currentUser.providerData.map(p => p.providerId);
};

// Login with custom token (for server integration)
export const loginWithCustomToken = async (token: string): Promise<UserCredential> => {
  try {
    // This would be implemented based on your backend token generation
    const error = new Error('Custom token login is not yet implemented');
    (error as any).code = 'auth/not-implemented';
    throw error;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Login with credential (for native platform integration)
export const loginWithCredential = async (credential: AuthCredential): Promise<UserCredential> => {
  try {
    const result = await signInWithCredential(auth, credential);
    await handleSocialAuthSuccess(result);
    return result;
  } catch (error: any) {
    throw formatAuthError(error);
  }
};
// Helper function to handle successful Apple auth with native name extraction
// Apple only provides the user's name on the FIRST sign-in, so we must capture it
const handleAppleAuthSuccess = async (
  firebaseUser: User,
  appleUser?: { displayName?: string | null; givenName?: string | null; familyName?: string | null }
): Promise<void> => {
  try {
    // Ensure Firebase Database is online
    try {
      if (database) {
        logger.info('Bringing Firebase database online after Apple Sign-In');
        goOnline(database);
      }
    } catch (goOnlineError) {
      logger.error('Error bringing database online:', goOnlineError);
    }

    // Check if user already exists in the database
    const existingUser = await getUserProfile(firebaseUser.uid);

    if (!existingUser) {
      // New user - must create profile
      // CRITICAL: Apple only provides name on FIRST sign-in, capture it now!
      let firstName = '';
      let lastName = '';
      let displayName = '';

      // Try to get name from Apple's response (preferred - matching original app behavior)
      if (appleUser?.givenName) {
        firstName = appleUser.givenName;
        logger.info('Got firstName from Apple:', firstName);
      }
      if (appleUser?.familyName) {
        lastName = appleUser.familyName;
        logger.info('Got lastName from Apple:', lastName);
      }

      // Fallback to displayName if parts not available
      if (!firstName && !lastName && appleUser?.displayName) {
        const nameParts = appleUser.displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      // Final fallback to Firebase user displayName
      if (!firstName && !lastName && firebaseUser.displayName) {
        const nameParts = firebaseUser.displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }

      displayName = [firstName, lastName].filter(Boolean).join(' ') || 'Marune User';

      // Generate unique username
      const baseUsername = firebaseUser.email
        ? firebaseUser.email.split('@')[0]
        : displayName.replace(/\s+/g, '').toLowerCase() || 'user';

      let username = baseUsername;
      let counter = 1;
      let isUsernameUnique = await isUsernameAvailable(username);

      while (!isUsernameUnique) {
        username = `${baseUsername}${counter}`;
        counter++;
        isUsernameUnique = await isUsernameAvailable(username);
      }

      // Create user profile - matching original app structure
      const userProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        username,
        usernameLower: username.toLowerCase(),
        firstName,
        lastName,
        displayName,
        displayNameLower: displayName.toLowerCase(),
        photoURL: firebaseUser.photoURL || '',
        profilePhotoURL: firebaseUser.photoURL || '',
        dateCreated: Date.now(),
        dateModified: Date.now(),
        emailVerified: firebaseUser.emailVerified,
        provider: 'apple.com',
        isPeakSub: false,
        preferredWeightUM: 'kg',
        // Fields matching original app
        bio: null,
        age: null,
        dateOfBirth: null,
        gender: null,
        instructor: null,
        school: null,
        beltDesignation: null,
        stripesCount: 0,
        pushToken: null,
        socialMediaProfiles: {
          facebook: '',
          instagram: '',
          twitter: '',
          youtube: '',
          website: ''
        },
        preferences: {
          theme: 'system',
          notifications: true,
          privacy: {
            profileVisibility: 'public',
            showTrainingSessions: true,
            showCompetitions: true
          }
        }
      };

      // Create username mapping
      const updates: Record<string, any> = {
        [`/users/${firebaseUser.uid}`]: userProfile,
        [`/usernames/${username}`]: { uid: firebaseUser.uid }
      };

      await updateMultipleSafe(updates);
      logger.info('Created Apple Sign-In user profile', { uid: firebaseUser.uid, username });

      // Verify profile was created
      const maxVerifyAttempts = 3;
      let profileVerified = false;

      for (let attempt = 1; attempt <= maxVerifyAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        const verifyProfile = await getUserProfile(firebaseUser.uid);
        if (verifyProfile && verifyProfile.uid === firebaseUser.uid) {
          logger.info(`✅ Apple auth profile verified on attempt ${attempt}`);
          profileVerified = true;
          break;
        }
      }

      if (!profileVerified) {
        logger.error('❌ Apple auth profile creation failed - attempting individual writes');
        try {
          await updateData(`/users/${firebaseUser.uid}`, userProfile);
          await updateData(`/usernames/${username}`, { uid: firebaseUser.uid });
          logger.info('✅ Retried Apple profile creation with individual updates');
        } catch (retryError) {
          logger.error('Retry Apple profile creation failed:', retryError);
        }
      }

      // Unlock new user perks
      try {
        await perksService.unlockNewUserPerks(firebaseUser.uid);
      } catch (perkError) {
        logger.error('Error unlocking new user perks:', perkError);
      }
    } else {
      // Existing user - just update login timestamp
      logger.info('Existing Apple user logging in', { uid: firebaseUser.uid });
      await updateData(`/users/${firebaseUser.uid}`, {
        dateModified: Date.now(),
        emailVerified: firebaseUser.emailVerified
      });
    }
  } catch (error) {
    logger.error('Error handling Apple auth success:', error);
    // Don't throw - let user continue even if profile update fails
  }
};

// Helper function to handle successful social auth
const handleSocialAuthSuccess = async (result: UserCredential): Promise<void> => {
  try {
    const { user } = result;
    
    // Ensure Firebase Database is online after successful social auth
    // This is critical for permissions to work properly
    try {
      if (database) {
        logger.info('Bringing Firebase database online after successful social auth');
        goOnline(database);
      }
    } catch (goOnlineError) {
      logger.error('Error bringing database online:', goOnlineError);
      // Continue anyway - authentication was successful
    }
    
    // Check if user already exists in the database
    const existingUser = await getUserProfile(user.uid);
    
    if (!existingUser) {
      // New user, create profile
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Generate a unique username from email or name
      const baseUsername = user.email 
        ? user.email.split('@')[0] 
        : displayName.replace(/\s+/g, '').toLowerCase();
        
      let username = baseUsername;
      let counter = 1;
      let isUsernameUnique = await isUsernameAvailable(username);
      
      // Keep appending numbers until we find a unique username
      while (!isUsernameUnique) {
        username = `${baseUsername}${counter}`;
        counter++;
        isUsernameUnique = await isUsernameAvailable(username);
      }
      
      // Create user profile
      const userProfile = {
        uid: user.uid,
        email: user.email,
        username,
        usernameLower: username.toLowerCase(),
        firstName,
        lastName,
        displayName: user.displayName,
        displayNameLower: user.displayName ? user.displayName.toLowerCase() : '',
        photoURL: user.photoURL,
        dateCreated: Date.now(),
        emailVerified: user.emailVerified,
        dateModified: Date.now(),
        provider: result.providerId,
        preferences: {
          theme: 'system',
          notifications: true,
          privacy: {
            profileVisibility: 'public'
          }
        }
      };
      
      // Create username mapping
      const updates: Record<string, any> = {
        [`/users/${user.uid}`]: userProfile,
        [`/usernames/${username}`]: { uid: user.uid }
      };

      // Use safer version to avoid root permission errors
      await updateMultipleSafe(updates);

      // CRITICAL FIX (Phase 1.3): Verify social auth profile was actually created
      // This catches silent failures in updateMultipleSafe for social login users
      const maxVerifyAttempts = 3;
      let socialProfileVerified = false;

      for (let attempt = 1; attempt <= maxVerifyAttempts; attempt++) {
        try {
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          const verifyProfile = await getUserProfile(user.uid);
          if (verifyProfile && verifyProfile.uid === user.uid) {
            logger.info(`✅ Social auth profile verified on attempt ${attempt}`);
            socialProfileVerified = true;
            break;
          }
        } catch (verifyError) {
          logger.warn(`Social profile verification attempt ${attempt} failed:`, verifyError);
        }
      }

      if (!socialProfileVerified) {
        logger.error('❌ Social auth profile creation failed - attempting individual writes');
        try {
          await updateData(`/users/${user.uid}`, userProfile);
          await updateData(`/usernames/${username}`, { uid: user.uid });
          logger.info('✅ Retried social profile creation with individual updates');
        } catch (retryError) {
          logger.error('Retry social profile creation failed:', retryError);
          // Don't throw here - let the user continue but they might have issues
        }
      }
    } else {
      // Existing user, update login timestamp
      await updateData(`/users/${user.uid}`, {
        dateModified: Date.now(),
        emailVerified: user.emailVerified
      });
    }
    
    // No additional token persistence needed - minimal auth handles it
  } catch (error) {
    logger.error('Error handling social auth success:', error);
    // We don't throw here to prevent blocking the login
  }
};
// Format auth errors for better user messages
export const formatAuthError = (error: any): AuthError => {
  const code = error.code || 'auth/unknown-error';
  let message = error.message || 'An unknown error occurred';
  
  // Replace Firebase error messages with more user-friendly ones
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      message = 'Invalid email or password';
      break;
    case 'auth/email-already-in-use':
      message = 'This email is already in use';
      break;
    case 'auth/weak-password':
      message = 'Password should be at least 6 characters';
      break;
    case 'auth/invalid-email':
      message = 'Please enter a valid email address';
      break;
    case 'auth/account-exists-with-different-credential':
      // Use custom message if provided (from our pre-check), otherwise use default
      if (error.message && error.message.includes('Please sign in with')) {
        message = error.message;
      } else {
        message = 'An account already exists with this email using a different sign-in method. Please sign in with your original method first, then link your accounts in Settings.';
      }
      break;
    case 'auth/operation-not-allowed':
      message = 'This sign-in method is not enabled for this project';
      break;
    case 'auth/too-many-requests':
      message = 'Too many requests. Please try again later';
      break;
    case 'auth/network-request-failed':
      message = 'Network error. Please check your connection';
      break;
    case 'auth/requires-recent-login':
      message = 'Please sign in again to complete this action';
      break;
    case 'auth/user-disabled':
      message = 'This account has been disabled';
      break;
    case 'auth/username-already-in-use':
      message = 'This username is already taken';
      break;
    default:
      // Try to extract cleaner message from Firebase error string
      if (typeof message === 'string' && message.includes('Firebase: ')) {
        message = message.replace('Firebase: ', '').replace(/ \(.+\)$/, '');
      }
  }
  
  return { code, message };
};